import { useParams } from "react-router-dom";
import { GuestTabletWizard } from "./components/GuestTabletWizard";
import { TabletDevicePendingScreen } from "./components/TabletDevicePendingScreen";
import { TabletPosCartOverlay } from "./components/TabletPosCartOverlay";
import { CampsiteBrandBar } from "@/components/branding/CampsiteBrandBar";
import { useBranding } from "@/lib/branding/useBranding";
import { TabletIdleScreen } from "./components/TabletIdleScreen";
import { useGuestTabletDevice } from "./useGuestTabletDevice";
import { useGuestTabletSession } from "./useGuestTabletSession";
import "./guest-tablet.css";

export function GuestTabletPage() {
  const { siteName: defaultSiteName } = useBranding();
  const { workstationCode = "reception-1" } = useParams<{ workstationCode: string }>();
  const {
    deviceStatus,
    loading: deviceLoading,
    error: deviceError,
    isApproved,
    refresh: refreshDevice,
    register: registerDevice,
  } = useGuestTabletDevice(workstationCode);
  const { data, error, loading, reload, applySession } = useGuestTabletSession(
    workstationCode,
    { enabled: isApproved },
  );

  if (!isApproved) {
    return (
      <div className="guest-tablet-root" data-kiosk="guest-tablet">
        <TabletDevicePendingScreen
          status={deviceStatus}
          loading={deviceLoading}
          error={deviceError}
          onRetry={() => void registerDevice().then(() => refreshDevice())}
        />
      </div>
    );
  }

  const isCheckInInProgress =
    data != null &&
    data.check_in_status !== "idle" &&
    data.check_in_status !== "check_in_complete" &&
    data.guest_display_step !== "idle";

  const showWizard = isCheckInInProgress && Boolean(data?.welcome);

  const showIdle =
    data != null &&
    !showWizard &&
    (data.check_in_status === "idle" ||
      data.guest_display_step === "idle" ||
      data.check_in_status === "check_in_complete" ||
      !data.welcome);

  const siteName = data?.site_name?.trim() || defaultSiteName;
  const logoUrl = data?.logo_url ?? null;
  const showPosDisplay = Boolean(data?.pos_cart && data.pos_cart.lines.length > 0);

  return (
    <div className="guest-tablet-root" data-kiosk="guest-tablet">
      <div
        className={
          showIdle && !showPosDisplay
            ? "guest-tablet-shell guest-tablet-shell--welcome"
            : "guest-tablet-shell"
        }
      >
        {data && showWizard ? (
          <CampsiteBrandBar
            siteName={siteName}
            logoUrl={logoUrl}
            className="shrink-0 border-b border-emerald-900/10 bg-white/80 dark:border-white/10 dark:bg-emerald-950/40"
            compact
          />
        ) : null}
        {loading && !data && (
          <div className="guest-tablet-idle">
            <p className="text-xl">Willkommen – Inhalte werden geladen…</p>
          </div>
        )}

        {error && (
          <div className="guest-tablet-card mb-6 border-red-200 bg-red-50 p-4 text-red-800 dark:bg-red-950/40 dark:text-red-100">
            {error}
            <button type="button" className="ml-4 underline" onClick={() => void reload()}>
              Erneut versuchen
            </button>
          </div>
        )}

        {showIdle && !showPosDisplay && (
          <TabletIdleScreen
            variant="welcome"
            idleScreen={
              data.idle_screen?.enabled
                ? data.idle_screen
                : {
                    enabled: true,
                    slideshow_interval_seconds: 8,
                    show_workstation_hint: true,
                    images: [],
                    text_blocks: [
                      {
                        id: "ready-headline",
                        body: "Willkommen",
                        variant: "headline",
                        sort_order: 1,
                      },
                      {
                        id: "ready-body",
                        body: `Sobald an der Rezeption eine Buchung für ${data.workstation_label} ausgewählt wird, erscheint hier Ihre persönliche Begrüßung.`,
                        variant: "body",
                        sort_order: 2,
                      },
                    ],
                  }
            }
            siteName={siteName}
            logoUrl={logoUrl}
            workstationLabel={data.workstation_label}
          />
        )}

        {showWizard ? (
          <GuestTabletWizard
            data={data}
            workstationCode={workstationCode}
            onSessionUpdate={applySession}
            onRefresh={() => void reload()}
          />
        ) : null}

        {showPosDisplay && data?.pos_cart ? (
          <TabletPosCartOverlay
            posCart={data.pos_cart}
            siteName={siteName}
            logoUrl={logoUrl}
          />
        ) : null}
      </div>
    </div>
  );
}
